import React, { useEffect, useState } from 'react';

interface ScreenReaderAnnouncerProps {
  /**
   * Message to announce to screen readers
   */
  message?: string;
  /**
   * Politeness level for screen reader announcement
   */
  assertive?: boolean;
  /**
   * Time in milliseconds to clear the announcement (for repeated announcements)
   */
  clearAfter?: number;
}

/**
 * A component that announces messages to screen readers using ARIA live regions.
 * This is useful for dynamic content changes, form submissions, and other important updates.
 */
export const ScreenReaderAnnouncer: React.FC<ScreenReaderAnnouncerProps> = ({
  message = '',
  assertive = false,
  clearAfter = 5000,
}) => {
  const [announcement, setAnnouncement] = useState(message);

  useEffect(() => {
    if (!message) return;
    
    // Set the announcement
    setAnnouncement(message);
    
    // Clear the announcement after the specified time
    // This allows the same message to be announced again if needed
    const timer = setTimeout(() => {
      setAnnouncement('');
    }, clearAfter);
    
    return () => clearTimeout(timer);
  }, [message, clearAfter]);

  return (
    <div 
      className="sr-only" 
      aria-live={assertive ? 'assertive' : 'polite'} 
      aria-atomic="true"
    >
      {announcement}
    </div>
  );
};

/**
 * Context and hook for using the screen reader announcer throughout the application
 */
import { createContext, useContext } from 'react';

interface AnnouncerContextType {
  announce: (message: string, assertive?: boolean) => void;
}

const AnnouncerContext = createContext<AnnouncerContextType | undefined>(undefined);

export const AnnouncerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState('');
  const [assertive, setAssertive] = useState(false);

  const announce = (text: string, isAssertive = false) => {
    setMessage(`${text} ${Date.now()}`); // Adding timestamp ensures announcement even if text is the same
    setAssertive(isAssertive);
  };

  return (
    <AnnouncerContext.Provider value={{ announce }}>
      {children}
      <ScreenReaderAnnouncer message={message} assertive={assertive} />
    </AnnouncerContext.Provider>
  );
};

export const useAnnouncer = (): AnnouncerContextType => {
  const context = useContext(AnnouncerContext);
  if (!context) {
    throw new Error('useAnnouncer must be used within an AnnouncerProvider');
  }
  return context;
};